import {defineStore} from "pinia";
import type {RentalDevice, RentalDeviceImage, RentalRequest} from "@/api/models/rental";
import {RentalRequestStatus} from "@/api/models/rental";
import ApiClient from "@/api/client/ApiClient";
import type {ApiMessage} from "@/api/models/common/ApiResponse";
import {handleApiError} from "@/api/helper";

interface RentalDevicesResponse extends ApiMessage {
  devices: RentalDevice[];
}

interface RentalDeviceResponse extends ApiMessage {
  device: RentalDevice | null;
}

interface RentalDeviceImageResponse extends ApiMessage {
  image: RentalDeviceImage | null;
}

interface RentalRequestsResponse extends ApiMessage {
  requests: RentalRequest[];
}

interface RentalRequestResponse extends ApiMessage {
  request: RentalRequest | null;
}

export const useRentalStore = defineStore({
  id: "rental",

  state() {
    return {
      rentalDevices: [] as RentalDevice[],
      rentalRequests: [] as RentalRequest[],
      myRentalRequests: [] as RentalRequest[],
      currentDevice: null as RentalDevice | null,
    };
  },

  actions: {
    // Rental Devices
    async fetchRentalDevices(force = false): Promise<RentalDevice[]> {
      if (!force && this.rentalDevices.length > 0) {
        return this.rentalDevices;
      }

      try {
        const response = await ApiClient.getJson<RentalDevicesResponse>("Rental/devices");
        if (!response.success) {
          throw new Error(response.message);
        }

        this.rentalDevices = response.devices || [];
        return this.rentalDevices;
      } catch (e: unknown) {
        console.error("Error fetching rental devices:", e);
        throw e;
      }
    },

    async fetchRentalDeviceById(id: number, force = false): Promise<RentalDevice | null> {
      if (!force && this.currentDevice && this.currentDevice.id === id) {
        return this.currentDevice;
      }

      try {
        const response = await ApiClient.getJson<RentalDeviceResponse>(`Rental/devices/${id}`);
        if (!response.success) {
          throw new Error(response.message);
        }

        this.currentDevice = response.device;
        return this.currentDevice;
      } catch (e: unknown) {
        console.error("Error fetching rental device:", e);
        throw e;
      }
    },

    async createRentalDevice(name: string, description: string | null, pricePerDay: number): Promise<ApiMessage> {
      try {
        const response = await ApiClient.postJson<RentalDeviceResponse>("Rental/devices", {
          name,
          description,
          pricePerDay,
        });

        if (response.success && response.device) {
          this.rentalDevices.unshift(response.device);
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    async updateRentalDevice(
      id: number,
      name: string,
      description: string | null,
      pricePerDay: number,
      isAvailable: boolean
    ): Promise<ApiMessage> {
      try {
        const response = await ApiClient.putJson<RentalDeviceResponse>(`Rental/devices/${id}`, {
          id,
          name,
          description,
          pricePerDay,
          isAvailable,
        });

        if (response.success && response.device) {
          const index = this.rentalDevices.findIndex((d) => d.id === id);
          if (index >= 0) {
            this.rentalDevices[index] = response.device;
          }
          if (this.currentDevice?.id === id) {
            this.currentDevice = response.device;
          }
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    async deleteRentalDevice(id: number): Promise<ApiMessage> {
      try {
        const response = await ApiClient.deleteJson(`Rental/devices/${id}`);

        if (response.success) {
          this.rentalDevices = this.rentalDevices.filter((d) => d.id !== id);
          if (this.currentDevice?.id === id) {
            this.currentDevice = null;
          }
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    // Rental Device Images
    async uploadDeviceImage(deviceId: number, file: File): Promise<ApiMessage> {
      try {
        const formData = new FormData();
        formData.append("RentalDeviceId", deviceId.toString());
        formData.append("File", file);

        const response = await fetch(
          `${process.env.VUE_APP_API_ENDPOINT}Rental/devices/${deviceId}/images`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );

        const result: RentalDeviceImageResponse = await response.json();

        if (result.success && result.image) {
          // Update the device's images in state
          const device = this.rentalDevices.find((d) => d.id === deviceId);
          if (device && device !== this.currentDevice) {
            device.images.push(result.image);
          }
          if (this.currentDevice && this.currentDevice.id === deviceId) {
            this.currentDevice.images.push(result.image);
          }
        }

        return result;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    async deleteDeviceImage(imageId: number, deviceId: number): Promise<ApiMessage> {
      try {
        const response = await ApiClient.deleteJson(`Rental/images/${imageId}`);

        if (response.success) {
          // Remove image from device in state
          const device = this.rentalDevices.find((d) => d.id === deviceId);
          if (device) {
            device.images = device.images.filter((i) => i.id !== imageId);
          }
          if (this.currentDevice?.id === deviceId) {
            this.currentDevice.images = this.currentDevice.images.filter((i) => i.id !== imageId);
          }
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    getImageUrl(imageId: number): string {
      return `${process.env.VUE_APP_API_ENDPOINT}Rental/images/${imageId}`;
    },

    // Rental Requests
    async createRentalRequest(
      rentalDeviceId: number,
      desiredStartDate: string,
      desiredEndDate: string,
      message: string | null
    ): Promise<ApiMessage> {
      try {
        const response = await ApiClient.postJson<RentalRequestResponse>("Rental/request", {
          rentalDeviceId,
          desiredStartDate,
          desiredEndDate,
          message,
        });

        if (response.success && response.request) {
          this.myRentalRequests.unshift(response.request);
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    async fetchRentalRequests(force = false): Promise<RentalRequest[]> {
      if (!force && this.rentalRequests.length > 0) {
        return this.rentalRequests;
      }

      try {
        const response = await ApiClient.getJson<RentalRequestsResponse>("Rental/requests");
        if (!response.success) {
          throw new Error(response.message);
        }

        this.rentalRequests = response.requests || [];
        return this.rentalRequests;
      } catch (e: unknown) {
        console.error("Error fetching rental requests:", e);
        throw e;
      }
    },

    async fetchMyRentalRequests(force = false): Promise<RentalRequest[]> {
      if (!force && this.myRentalRequests.length > 0) {
        return this.myRentalRequests;
      }

      try {
        const response = await ApiClient.getJson<RentalRequestsResponse>("Rental/requests/my");
        if (!response.success) {
          throw new Error(response.message);
        }

        this.myRentalRequests = response.requests || [];
        return this.myRentalRequests;
      } catch (e: unknown) {
        console.error("Error fetching my rental requests:", e);
        throw e;
      }
    },

    async updateRequestStatus(
      requestId: number,
      status: RentalRequestStatus,
      adminNotes: string | null
    ): Promise<ApiMessage> {
      try {
        const response = await ApiClient.putJson<RentalRequestResponse>(`Rental/requests/${requestId}/status`, {
          requestId,
          status,
          adminNotes,
        });

        if (response.success && response.request) {
          const index = this.rentalRequests.findIndex((r) => r.id === requestId);
          if (index >= 0) {
            this.rentalRequests[index] = response.request;
          }
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    clearState(): void {
      this.rentalDevices = [];
      this.rentalRequests = [];
      this.myRentalRequests = [];
      this.currentDevice = null;
    },
  },
});
